import { phone } from 'phone';
import { AnyObject, NumberSchema, StringSchema } from 'yup';

import { BigNumber } from '@frontend/numeric';

import { i18n } from '@aqx-core/common/i18n';

import { EPasswordCriteriaType, PASSWORD_LENGTH } from '../constants/password';

export * from './device';

export const isEmptyObject = (obj: Record<string, unknown>) => JSON.stringify(obj) === '{}';

export const hasHttpProtocol = (val: string) => /https?:\/\//.test(val);

export const validateDigitsOnly = (number: string) => {
  const onlyDigitsRegex = /^\d*\.?\d*$/;

  return onlyDigitsRegex.test(number);
};

export const isEmpty = (value: string | number | undefined) => {
  if (typeof value === 'string') {
    return value === '';
  }

  return value === undefined || value === null;
};

export const hasWhitespace = (value: string) => {
  return /\s/g.test(value);
};

export const isAlphaNumeric = (value: string) => {
  return /^(?=.*[a-zA-Z])(?=.*[0-9])/.test(value);
};

export const containsOnlyNumbers = (value: string) => {
  return /^[0-9]+$/.test(value);
};

export const EMAIL_REGEX =
  /^[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/;

export const isValidEmail = (value: string) => {
  return EMAIL_REGEX.test(value);
};

export const isValidPasswordLength = (value: string, min: number, max: number) =>
  typeof value === 'string' && new RegExp(`^.{${min},${max}}$`).test(value);

export const hasLowercase = (value: string) => typeof value === 'string' && /[a-z]/.test(value);

export const hasUppercase = (value: string) => typeof value === 'string' && /[A-Z]/.test(value);

export const hasNumber = (value: string) => typeof value === 'string' && /[0-9]/.test(value);

export const hasSpecialCharacters = (value: string) =>
  typeof value === 'string' && /[-!#$%&'"*+@^()_=+;:,.`~?<>\/{}\[\]]/.test(value);

export const validatePassword = (value: string, minLength: number, maxLength: number) => {
  const lengthValid = isValidPasswordLength(value, minLength, maxLength);
  const hasLowercaseChar = hasLowercase(value);
  const hasUppercaseChar = hasUppercase(value);
  const hasNumericChar = hasNumber(value);
  const hasSpecialChar = hasSpecialCharacters(value);
  return lengthValid && hasLowercaseChar && hasUppercaseChar && hasNumericChar && hasSpecialChar;
};

export const validatePasswordForLirunexCrm = (value: string) => {
  const { min, max } = PASSWORD_LENGTH[EPasswordCriteriaType.LIRUNEX_CRM];
  return validatePassword(value, min, max);
};

export const validatePasswordForMitraderCrm = (value: string) => {
  const { min, max } = PASSWORD_LENGTH[EPasswordCriteriaType.MITRADER_CRM];
  return validatePassword(value, min, max);
};

export const validatePasswordForMetaTraderPlatform = (value: string) => {
  const { min, max } = PASSWORD_LENGTH[EPasswordCriteriaType.METATRADER_PLATFORM];
  return validatePassword(value, min, max);
};

export const validateName = (name: string) => {
  const nameRegex = /^(?=.*\p{L})[\p{L}\d\s'-]+$/u;

  return nameRegex.test(name);
};

export const validateUsername = (userName: string) => {
  const usernameRegex = /^\S+$/;

  return usernameRegex.test(userName);
};

export const isValidHexColor = (hexCode?: string) => {
  if (!hexCode || hexCode === '') {
    return false;
  }

  return /^#?([0-9A-F]{3}){1,2}$/i.test(hexCode);
};

export const isValidUrl = (value: string) => {
  if (!value.startsWith('http://') && !value.startsWith('https://')) {
    return false;
  }

  const domain = 'example.com';
  // Replace all {{domain}} in the URL with the sample domain
  const valueWithSampleDomain = value.replace(/\{\{domain\}\}/g, domain);
  // Replace {{}} template placeholders with a valid placeholder for validation, it will fail if user provide {language} <-- single {}
  const urlForValidation = valueWithSampleDomain.replace(/\{\{[^}]+\}\}/g, 'placeholder');

  const pattern = new RegExp(
    '^(https?:\\/\\/)' + // protocol (required)
      '(?:([^:@\\s{}]+):([^@\\s{}]+)@)?' + // optional user:password@ (excludes single {})
      '(?:' +
      '([a-z\\d]([a-z\\d-]*[a-z\\d])*\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3})|' + // IPv4 address
      '\\[([a-f\\d:]+)\\]' + // IPv6 address in brackets
      ')' +
      '(?::(\\d+))?' + // optional port
      '(?:\\/([^?#\\s{}]*))?' + // optional path (excludes single {})
      '(?:\\?([^#\\s{}]*))?' + // optional query string (excludes single {})
      '(?:#([^\\s{}]*))?$', // optional fragment (excludes single {})
    'i'
  );

  return pattern.test(urlForValidation);
};

export const isValidPhoneNumber = (dialCode: string, localPhoneNumber: string) => {
  // Special handling for SG numbers
  if (dialCode === '+65' && localPhoneNumber.length > 8) {
    return false;
  }

  const fullNumber = dialCode + localPhoneNumber;
  const validationResults = phone(fullNumber);

  return dialCode === validationResults.countryCode && validationResults.isValid;
};

export const isPropInvalid = (prop: unknown) => {
  return (
    typeof prop === 'undefined' ||
    prop === null ||
    (typeof prop === 'string' && prop.toLowerCase() === 'null') ||
    (typeof prop === 'object' && prop && Object.entries(prop).length === 0 && prop.constructor === Object)
  );
};

export const validateDecimalString = (schema: StringSchema<string | null | undefined>) => {
  return schema
    .matches(/^\d*(?:\.\d+)?$/, ({ label }) =>
      label ? i18n.t('{{label}} is invalid', { label }) : i18n.t('Field is invalid')
    )
    .matches(/^\d*(?:\.\d{1,2})?$/, i18n.t('Only a maximum of 2 d.p. allowed'));
};

export const getRequiredText = ({ label }: { label: string }) =>
  label ? i18n.t('{{label}} is required', { label }) : i18n.t('Field is required');

export const getInvalidText = ({ label }: { label: string }) =>
  label ? i18n.t('{{label}} is invalid', { label }) : i18n.t('Field value is invalid');

export const getEnterValidText = ({ label }: { label: string }) => i18n.t('Please enter a valid {{label}}.', { label });

export const getSelectText = ({ label }: { label: string }) => i18n.t('Please select a {{label}}.', { label });

// export const getInvalidMultiplyQuantityText = () => i18n.t('Invalid order size (not multiple of lot size)');

export const getInvalidQuantityText = () => i18n.t('Invalid order size');

export const getLessThanMinVolumeText = (minVolume: number, sizeLabel?: string) =>
  sizeLabel
    ? i18n.t('Minimum {{sizeLabel}} is {{minVolume}}', { minVolume, sizeLabel })
    : i18n.t('Minimum is {{value}}', { value: minVolume });

export const getMoreThanMaxVolumeText = (maxVolume: number, sizeLabel?: string) =>
  sizeLabel
    ? i18n.t('Maximum {{sizeLabel}} is {{maxVolume}}', { maxVolume, sizeLabel })
    : i18n.t('Maximum is {{value}}', { value: maxVolume });

export const getMinimumTargetValueText = (minValue: string, isPercentCondition?: boolean) =>
  isPercentCondition
    ? i18n.t('The minimum % is {{minValue}}', { minValue })
    : i18n.t('The minimum price is {{minValue}}', { minValue });

export const getMaximumTargetValueText = (maxValue: string, isPercentCondition?: boolean) =>
  isPercentCondition
    ? i18n.t('The maximum % is {{maxValue}}', { maxValue })
    : i18n.t('The maximum price is {{maxValue}}', { maxValue });

export const referenceNumberRequiredMinCheck = (
  [refValue]: (number | null | undefined)[],
  schema: NumberSchema<number | null | undefined, AnyObject, undefined, ''>
) => {
  if (refValue === null || refValue === undefined) {
    return schema;
  }
  return schema.required(getRequiredText).min(refValue, ({ min }) => i18n.t('Value must be at least {{min}}', { min }));
};

export const referenceNumberRequiredCheck = (
  [refValue]: (number | null | undefined)[],
  schema: NumberSchema<number | null | undefined, AnyObject, undefined, ''>
) => {
  if (refValue === null || refValue === undefined) {
    return schema;
  }
  return schema.required(getRequiredText);
};

export const transformInvalidNumberToNull = (currentValue: number | string | null | undefined) =>
  currentValue === '' || Number.isNaN(currentValue) ? null : currentValue;

export const validNumberStringTest = (value?: string, allowZero = true) => {
  // Check if the value is null, undefined, or empty string
  if (value === null || value === undefined || value === '') {
    return true;
  }
  if (value === '0' && allowZero === false) {
    return false;
  }

  // Check if the value ends with a decimal point
  if (value.endsWith('.')) {
    return false;
  }

  // Check if the value is a finite number using BigNumber
  return new BigNumber(value).isFinite();
};

export const validMultiplyQuantityTest = ({
  value,
  lotSizeStep,
  minContractLotSize
}: {
  value?: string;
  lotSizeStep?: number;
  minContractLotSize?: number;
}) => {
  if (!value || !lotSizeStep) {
    return false;
  }
  const bnValue = new BigNumber(value);
  const bnLotSizeStep = new BigNumber(lotSizeStep);

  const bnMinContractLotSize = minContractLotSize ? new BigNumber(minContractLotSize) : null;
  // Check if the value is valid and positive
  if (!bnValue.isFinite() || bnValue.lte(0)) {
    return false;
  }
  // Check if the value is greater than or equal to minContractLotSize
  if (bnMinContractLotSize !== undefined && bnMinContractLotSize !== null && bnValue.lt(bnMinContractLotSize)) {
    return false;
  }
  // Calculate the ratio and check if it's an integer
  const ratio = bnValue.div(bnLotSizeStep);
  return ratio.isInteger();
};

export const validMinQuantityTest = ({ value, lotSize }: { value?: string; lotSize?: number }) => {
  if (value && lotSize) {
    return Number(value) >= Math.abs(lotSize);
  } else {
    return false;
  }
};

export const validMaxQuantityTest = ({ value, lotSize }: { value?: string; lotSize?: number }) => {
  if (value && lotSize) {
    return Number(value) <= Math.abs(lotSize);
  } else {
    return false;
  }
};

export const isTruthyOrZero = <T>(value?: T): value is NonNullable<T> => {
  return value === 0 || !!value;
};
